<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Helpers
	import { real_id } from '../../../../helpers/interface_helpers';
	import { valueOutOfRange } from '../../../../helpers/utilities';
	import { onSettingsResponse } from '../../../../helpers/device_settings_helpers';
	import {
		buildIlluminanceSetConfigParams,
		usesVentoraMergedSensorSetConfig,
	} from '../../../../helpers/sensor_config_helpers';
	//Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Stores
	import { getDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Design system components
	import { Button, Input, Text } from '@Shelly/shelly-design-system';
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	import type { Device } from '../../../../types/Device';

	export let device: Device, type: string;

	const deviceId = real_id(device.id);
	const settings = readDeviceSettings(deviceId);

	let value = settings?.['illuminance:0']?.[`${type}_thr`];
	let cacheValue = value;
	let loading = false;

	const min = 0;
	const max = 1000;
	const rangeLabel = $format('min_max_illuminance_thr', {
		default: '{min} - {max} lux',
		values: { min, max },
	});

	$: error = valueOutOfRange(value, min, max)
		? $format('min_max_error_description', {
				values: { min, max },
				default: 'Value must be between {min} and {max}.',
			})
		: '';

	const save = async () => {
		if (cacheValue !== value) {
			loading = true;
			const thresholdKey = `${type}_thr` as 'dark_thr' | 'bright_thr';
			const params = usesVentoraMergedSensorSetConfig(device)
				? buildIlluminanceSetConfigParams(settings, device.channel, {
						[thresholdKey]: value,
					})
				: { config: { [thresholdKey]: value }, id: device.channel };
			const res: SettingRequest = await gen2Request(device, 'Illuminance.SetConfig', params);
			const response = onSettingsResponse(res, device);
			if (response) {
				devices_settings.update_devices_settings(deviceId, {
					...settings,
					'illuminance:0': {
						...(settings?.['illuminance:0'] ?? {}),
						[thresholdKey]: value,
					},
				});
				cacheValue = value;
			} else {
				value = cacheValue;
			}
			loading = false;
		}
	};
</script>

<Text styleType="body-small" class="mb-s">
	{$format(`${type}_threshold_description`, {
		default: `Illuminance values below this threshold will be considered ${type}`,
	})}</Text
>

<Input
	class="mb-s"
	size="m"
	bind:value
	type={'number'}
	{min}
	{max}
	{error}
	label={rangeLabel}
	id="input-u53200n"
	aria-labelledby="input-u53200n"
/>
<Button size="m" width="full" disabled={error} {loading} on:click={save}>
	{$format('button_save', { default: 'Save' })}
</Button>
